import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { X } from 'lucide-react';
import './RouteMapModal.scss';

const GEOAPIFY_API_KEY = process.env.REACT_APP_GEOAPIFY_API_KEY || '0b5e67f15eac4c92af69064fddd28139';

const geocodeAddress = async (address) => {
  if (!GEOAPIFY_API_KEY || !address || !address.trim()) return null;
  try {
    const url = `https://api.geoapify.com/v1/geocode/search?text=${encodeURIComponent(address)}&format=json&limit=1&apiKey=${GEOAPIFY_API_KEY}`;
    const res = await fetch(url);
    const data = await res.json();
    const first = data.results && data.results[0];
    return first ? { lat: first.lat, lng: first.lon } : null;
  } catch (err) {
    console.error('[RouteMapModal] Geocode error:', err);
    return null;
  }
};

const fetchRoute = async (from, to) => {
  if (!GEOAPIFY_API_KEY || !from || !to) return null;
  try {
    const waypoints = `${from.lat},${from.lng}|${to.lat},${to.lng}`;
    const url = `https://api.geoapify.com/v1/routing?waypoints=${waypoints}&mode=drive&apiKey=${GEOAPIFY_API_KEY}`;
    const res = await fetch(url);
    const data = await res.json();
    if (data.features && data.features.length > 0) return data;
    return null;
  } catch (err) {
    console.error('[RouteMapModal] Routing error:', err);
    return null;
  }
};

const RouteMapModal = ({ isOpen, onClose, pickup, delivery }) => {
  const mapContainerRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const routeLayerRef = useRef(null);
  const markersLayerRef = useRef(null);

  const [pickupCoords, setPickupCoords] = useState(null);
  const [deliveryCoords, setDeliveryCoords] = useState(null);
  const [routeGeoJson, setRouteGeoJson] = useState(null);
  const [mapLoading, setMapLoading] = useState(true);
  const [routeError, setRouteError] = useState(null);

  const getFullAddress = (location) => {
    if (!location) return '';
    const address = location.address || {};
    const parts = [];
    if (address.address || location.address) {
      parts.push(typeof address.address === 'string' ? address.address : location.address);
    }
    if (address.city || location.city) parts.push(address.city || location.city);
    if (address.state || location.state) parts.push(address.state || location.state);
    if (address.zipCode || address.zip || location.zipCode || location.zip) {
      parts.push(address.zipCode || address.zip || location.zipCode || location.zip);
    }
    return parts.join(', ');
  };

  const pickupAddress = getFullAddress(pickup);
  const deliveryAddress = getFullAddress(delivery);

  const getCoordsFromLocation = (loc) => {
    if (!loc) return null;
    const lat = loc.lat ?? loc.address?.lat;
    const lng = loc.lng ?? loc.address?.lng;
    if (lat != null && lng != null) return { lat: Number(lat), lng: Number(lng) };
    return null;
  };

  useEffect(() => {
    if (!isOpen || !pickup || !delivery) return;

    let cancelled = false;
    setMapLoading(true);
    setRouteError(null);

    const run = async () => {
      const from = getCoordsFromLocation(pickup) || await geocodeAddress(pickupAddress);
      if (cancelled) return;
      const to = getCoordsFromLocation(delivery) || await geocodeAddress(deliveryAddress);
      if (cancelled) return;

      setPickupCoords(from);
      setDeliveryCoords(to);

      if (!from || !to) {
        setRouteError('Could not resolve pickup or delivery address.');
        setMapLoading(false);
        return;
      }

      const route = await fetchRoute(from, to);
      if (cancelled) return;
      setRouteGeoJson(route);
      setMapLoading(false);
    };

    run();
    return () => { cancelled = true; };
  }, [isOpen, pickup, delivery, pickupAddress, deliveryAddress]);

  useEffect(() => {
    if (!isOpen || !GEOAPIFY_API_KEY) return;
    const id = setTimeout(() => {
      const container = mapContainerRef.current;
      if (!container) return;

      const center = pickupCoords && deliveryCoords
      ? { lat: (pickupCoords.lat + deliveryCoords.lat) / 2, lng: (pickupCoords.lng + deliveryCoords.lng) / 2 }
      : { lat: 39.8283, lng: -98.5795 };

    const map = L.map(container).setView([center.lat, center.lng], pickupCoords && deliveryCoords ? 6 : 4);
    mapInstanceRef.current = map;

    const isRetina = window.matchMedia('(min-resolution: 2dppx)').matches;
    const tileUrl = isRetina
      ? `https://maps.geoapify.com/v1/tile/osm-bright/{z}/{x}/{y}@2x.png?apiKey=${GEOAPIFY_API_KEY}`
      : `https://maps.geoapify.com/v1/tile/osm-bright/{z}/{x}/{y}.png?apiKey=${GEOAPIFY_API_KEY}`;
    L.tileLayer(tileUrl, {
      attribution: 'Powered by <a href="https://www.geoapify.com/" target="_blank">Geoapify</a> | © OpenStreetMap contributors',
      maxZoom: 20,
    }).addTo(map);
    }, 0);

    return () => {
      clearTimeout(id);
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
        routeLayerRef.current = null;
        markersLayerRef.current = null;
      }
    };
  }, [isOpen, GEOAPIFY_API_KEY]);

  useEffect(() => {
    if (!isOpen && mapInstanceRef.current) {
      mapInstanceRef.current.remove();
      mapInstanceRef.current = null;
      routeLayerRef.current = null;
      markersLayerRef.current = null;
    }
  }, [isOpen]);

  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map || (!pickupCoords && !deliveryCoords)) return;

    if (markersLayerRef.current) {
      map.removeLayer(markersLayerRef.current);
      markersLayerRef.current = null;
    }

    const group = L.layerGroup();
    if (pickupCoords) {
      L.circleMarker([pickupCoords.lat, pickupCoords.lng], {
        radius: 10,
        fillColor: '#e74c3c',
        color: '#fff',
        weight: 2,
        fillOpacity: 1,
      }).bindTooltip('Pickup', { permanent: false }).addTo(group);
    }
    if (deliveryCoords) {
      L.circleMarker([deliveryCoords.lat, deliveryCoords.lng], {
        radius: 10,
        fillColor: '#27ae60',
        color: '#fff',
        weight: 2,
        fillOpacity: 1,
      }).bindTooltip('Delivery', { permanent: false }).addTo(group);
    }
    group.addTo(map);
    markersLayerRef.current = group;
  }, [pickupCoords, deliveryCoords]);

  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map) return;

    if (routeLayerRef.current) {
      map.removeLayer(routeLayerRef.current);
      routeLayerRef.current = null;
    }

    if (routeGeoJson && routeGeoJson.features && routeGeoJson.features.length > 0) {
      const routeLayer = L.geoJSON(routeGeoJson, {
        style: () => ({ color: 'rgba(20, 137, 255, 0.8)', weight: 5 }),
      }).addTo(map);
      routeLayerRef.current = routeLayer;
      const bounds = routeLayer.getBounds();
      if (bounds.isValid()) map.fitBounds(bounds, { padding: [40, 40] });
    }
  }, [routeGeoJson]);

  if (!isOpen) return null;

  const modalContent = (
    <div className="route-map-modal-overlay" onClick={onClose}>
      <div className="route-map-modal" onClick={(e) => e.stopPropagation()}>
        <div className="route-map-modal-header">
          <h2>Route Map</h2>
          <button type="button" className="close-btn" onClick={onClose}>
            <X size={24} />
          </button>
        </div>
        <div className="route-map-modal-body">
          <div className="route-info">
            <div className="route-location pickup">
              <span className="location-icon">📍</span>
              <div>
                <strong>Pickup:</strong> {pickupAddress || 'Not specified'}
                {pickup?.locationName && <div className="location-name">{pickup.locationName}</div>}
              </div>
            </div>
            <div className="route-location delivery">
              <span className="location-icon">🏁</span>
              <div>
                <strong>Delivery:</strong> {deliveryAddress || 'Not specified'}
                {delivery?.locationName && <div className="location-name">{delivery.locationName}</div>}
              </div>
            </div>
          </div>
          <div className="route-map-container">
            {!GEOAPIFY_API_KEY ? (
              <div className="route-map-error">
                <p>Geoapify API key is not configured.</p>
                <p>Please set REACT_APP_GEOAPIFY_API_KEY in your environment variables.</p>
              </div>
            ) : (
              <>
                <div ref={mapContainerRef} className="route-map-leaflet" style={{ width: '100%', height: '100%', minHeight: '500px' }} />
                {mapLoading && (
                  <div className="route-map-loading">Loading map...</div>
                )}
                {routeError && (
                  <div className="route-map-error-overlay">{routeError}</div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default RouteMapModal;
